import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash-es';

import { type Entitlement, selectAbility } from '@/shared/entitlements';

import { AppState } from '../helper';

/** Every role but `user`: each is assigned by hand and never counts as a free user. */
const SPECIAL_ROLES = new Set(['admin', 'partner', 'host', 'finance', 'staff', 'moderator']);

/**
 * Everything below answers with a boolean, so a component subscribes to its own answer rather than
 * to the whole payload. The ability itself lives in `@/shared/entitlements` — reach for `useCan`
 * there only when the id varies per item.
 */
const selectCan = (state: AppState) => selectAbility(state).can;

export const selectIsSpecialRole = createSelector(
  (state: AppState) => state.user.role,
  (role) => SPECIAL_ROLES.has(role as string),
);

export const selectIsAdmin = createSelector(
  (state: AppState) => state.user.role,
  (role) => role === 'admin',
);

export const selectIsPartner = createSelector(
  (state: AppState) => state.user.role,
  (role) => role === 'partner',
);

export const selectIsHost = createSelector(
  (state: AppState) => state.user.role,
  (role) => role === 'host',
);

// The zoo a Partner is tied to (single zoo, full edit access). Empty for non-partners.
export const selectPartnerZooId = (state: AppState) => state.user.zooId;

// The habitats a Host is allowed to manage (habitat-scoped). Stable empty array for non-hosts.
const EMPTY_HABITAT_IDS: string[] = [];
export const selectUserHabitatIds = (state: AppState) => state.user.habitats ?? EMPTY_HABITAT_IDS;

export const selectIsFreeUser = createSelector(
  (state: AppState) => state.user.role,
  (state: AppState) => state.user.userProducts,
  (role, userProducts) => {
    if (SPECIAL_ROLES.has(role as string)) {
      return false;
    }
    // `userProducts` is the live holdings, so empty means nothing held right now.
    return !userProducts?.length;
  },
);

/**
 * Each asks "may they do this thing", not "do they hold this plan": a day-pass, supporter or TZ Plus
 * holder passes `highlights.full` without holding Explorer. `tour.default` and `tour.tzPlus` are
 * disjoint, which is what lets the two tours run in sequence (ADR 0012 in this repo).
 *
 * A role grants the viewing set in the model itself, so no call site adds `|| isSpecialRole`.
 */
export const selectHasFullHighlights = (state: AppState) => selectCan(state)('highlights.full');
export const selectHasEducatorFeature = (state: AppState) => selectCan(state)('resource.access');
export const selectHasGuidedExperienceFeature = (state: AppState) => selectCan(state)('tour.default');
export const selectHasTzPlusFeature = (state: AppState) => selectCan(state)('tour.tzPlus');
export const selectCanUseScheduleFilters = (state: AppState) => selectCan(state)('schedule.filters');
export const selectCanUseAlbumFilters = (state: AppState) => selectCan(state)('album.filters');
export const selectCanSearchAllHabitats = (state: AppState) => selectCan(state)('media.globalSearch');

/**
 * Whether they hold something subscription-like.
 *
 * Not an entitlement: it says nothing about what somebody may do, only that a second holding of the
 * same **Product Family** would be refused. The API sends it per holding, from the product's own
 * `oneLivePer`.
 */
export const selectHasExclusiveProduct = createSelector(
  (state: AppState) => state.user.userProducts,
  (userProducts) => !!userProducts?.some((held) => held.exclusive),
);

export const createSelectCanAccessHabitat = _.memoize(
  (habitatId: string | undefined) => (state: AppState) =>
    habitatId ? selectCan(state)('habitat.access', habitatId) : false,
);

export const selectCanDownloadMedia = (state: AppState) => !selectIsFreeUser(state);

/**
 * The habitat a check is about when nobody passes one.
 *
 * Every one of these is asked from inside a habitat page, so the open habitat is read here once
 * rather than passed by every call site.
 */
const selectCurrentHabitatId = (state: AppState) => state.habitat?.habitatInfo?._id;

const createSelectCanHere = (entitlement: Entitlement) => (state: AppState) =>
  selectCan(state)(entitlement, selectCurrentHabitatId(state));

/**
 * Whether they may create events anywhere at all — gates Add Event, the My Events scope control and
 * the inline card actions. Which habitats is asked per habitat, where the card or picker has one.
 */
export const selectCanManageEvents = (state: AppState) => {
  const verdict = selectAbility(state).explain('event.create');
  return verdict === 'granted' || verdict === 'out-of-scope';
};

/** Whether they may create events in the open habitat — the habitat page's Add Event button. */
export const selectCanManageCurrentHabitatEvents = createSelectCanHere('event.create');

/**
 * Habitat-scoped checks for the habitat in view. The mapping from the old RBAC permissions is in
 * `docs/spec/entitlements.md` (zl-api).
 */
export const selectCanUploadHere = createSelectCanHere('media.upload');
export const selectCanModerateChatHere = createSelectCanHere('chat.moderate');
export const selectCanControlCameraHere = createSelectCanHere('habitat.cameraMode');
export const selectCanPowerStreamHere = createSelectCanHere('habitat.streamPower');
export const selectCanEditHabitatMetaHere = createSelectCanHere('habitat.editHabitatMeta');
export const selectCanEditCardsHere = createSelectCanHere('habitat.cardsEdit');
export const selectCanCreateEventsHere = createSelectCanHere('event.create');
export const selectCanHostHere = createSelectCanHere('habitat.host');
export const selectCanAccessCurrentHabitat = createSelectCanHere('habitat.access');
/** The link to a partner's own zoo analytics. Admin holds every entitlement but is not a partner, so
 * it is left out here, as the RBAC always did. */
export const selectCanViewPartnerAnalytics = (state: AppState) =>
  selectCan(state)('analytics.partner') && !selectIsAdmin(state);

/**
 * Holding an entitlement *somewhere*, without naming where.
 *
 * Only for the pages that are not inside a habitat — the home and highlights feeds, where the
 * question is "does this person curate anything at all", not "may they act here". Everywhere else
 * names the habitat, because holding something somewhere is not holding it here.
 */
const selectEntitlementsHeldSomewhere = createSelector(
  (state: AppState) => state.user.entitlements,
  (entitlements) => new Set(entitlements ?? []),
);

/** Whether the hidden highlight types — curated, and the rest of the content-team list — show. */
export const selectCuratesHighlights = (state: AppState) => selectEntitlementsHeldSomewhere(state).has('media.upload');

/**
 * The gear button, and the panel behind it.
 *
 * Its modal controls exactly two things — stream power and the camera mode — so holding either is
 * what should open it. Moderators lose it here: they held `EditStream` over every habitat, which
 * put a live-stream panel in front of somebody whose job is the chat.
 */
export const selectShowsStreamControls = (state: AppState) =>
  selectCanPowerStreamHere(state) || selectCanControlCameraHere(state);

/**
 * Creating events in a *named* habitat rather than the open one — the schedule cards, which each
 * carry their own habitat.
 */
export const createSelectCanCreateEventsIn = _.memoize(
  (habitatId: string | undefined) => (state: AppState) => (habitatId ? selectCan(state)('event.create', habitatId) : false),
);

/**
 * Whether to point somebody at the educator app.
 *
 * **Personalisation, not a gate.** The app itself is open — its gated parts (Google Classroom, doc
 * download, the slideshow) ask for `resource.access` where they are — so this only decides whether
 * we show somebody the way in, and a self-declared **Persona** is a perfectly good reason to.
 *
 * Renamed from `selectShowsEducatorsApp`: `can` is for entitlements, and reading this as a
 * permission suggests the educator app is locked, which it is not.
 */
export const selectShowsEducatorsApp = createSelector(
  selectHasEducatorFeature,
  (state: AppState) => state.user.persona,
  (hasEducatorTools, persona) => hasEducatorTools || persona === 'educator',
);

/** Personalisation, not a gate: the Family Room is open to anyone, and a parent is only shown the way in. */
export const selectShowsFamilyRoom = (state: AppState) => state.user.persona === 'parent';

/**
 * Whether the Educator Profile stands in the way of a complete profile. True when it has been
 * answered — and true for every other persona, who is never asked for one. Named for what it
 * decides rather than "completed", which would be a lie for every user who has no Educator Profile
 * to complete.
 */
export const selectEducatorProfileIsNotBlocking = createSelector(
  (state: AppState) => state.user.persona,
  (state: AppState) => state.user.educatorDetails,
  (persona, educatorDetails) =>
    persona !== 'educator' ||
    !!(educatorDetails?.location && educatorDetails.grades?.length && educatorDetails.teachingType),
);

/**
 * What the "Complete profile" badge is asking for — and, for an educator, what the Profile
 * Completed Reward is gated on.
 *
 * Deliberately stricter than what the About Me form requires to submit: saving stays possible with
 * an unanswered Educator Profile, so somebody fixing a typo in their name is not held hostage by
 * three unrelated questions. Only the badge and the reward read this.
 */
export const selectIsProfileCompleted = createSelector(
  (state: AppState) => state.user.name,
  (state: AppState) => state.user.ageGroup,
  (state: AppState) => state.user.persona,
  selectEducatorProfileIsNotBlocking,
  (name, ageGroup, persona, educatorProfileIsNotBlocking) =>
    !!name && !!ageGroup && !!persona && educatorProfileIsNotBlocking,
);

export const selectFavoriteHabitatIds = createSelector(
  (state: AppState) => state.user.favoriteHabitats,
  (favoriteHabitatIds) => new Set(favoriteHabitatIds ?? []),
);

// Whether the user already asked to be reminded of this event. A boolean rather than the raw
// `scheduledEvents` list, so a component subscribes to the one event it cares about.
export const selectIsRemindedOfEvent = (scheduleId: string) => (state: AppState) =>
  !!state.user.scheduledEvents?.some((event) => event.scheduleId === scheduleId);
